import { Tooltip, Typography, Grid } from '@mui/material';
import { Box } from '@mui/system';

import { useCallback, useState } from 'react';

import { useDispatch } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import NotificationStatusIcon from 'components/Notification/NotificationStatusIcon';
import moment from 'moment';

import { createTask } from 'common/api';
import { ICreateTask, ITask } from 'common/api/type-def';
import ButtonComp from 'common/components/Button/ButtonComp';
import DatepickerComponent, {
	displayDateFormat,
} from 'common/components/Datepicker/Datepicker';
import SelectDropdown, {
	ISelectOption,
} from 'common/components/Select/SelectDropdown';
import SelectableHOC from 'common/components/SelectableHOC/SelectableHOC';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import { MODULES, MODULE_NAME, TASKTYPE } from 'common/constants';
import { PROGRESS_BAR_ELEMENTS } from 'common/constants/CitationDetails';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { LoggedInRole } from 'common/constants/UserManagementConstants';
import {
	setRequirementInventoryBreadcrumbText,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setWorkflowFeedback } from 'common/store/reducer/simulationReducer';
import { isRoleExists } from 'common/utils/authCheck';
import {
	generateToastMessage,
	getDateStringFromISOString,
	getISODateString,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { updateBulkTask } from 'features/RegulationInventory/api';
import {
	IRegulationInventoryList,
	IRegInvOwnerInfo,
} from 'features/RegulationInventory/api/type-def';
import { getRegInvOwnerData } from 'features/RegulationInventory/RegInventoryUtils';

import styles from './AllRegulationTable.module.scss';

const { REQUIREMENT_INVENTORY_NAME } = MODULE_NAME;

const { OK } = HTTP_STATUS_CODES;

const { WORKFLOW } = TASKTYPE;

const { COMPLETE, APPLICABILITY_ASSESSMENT, APPLICABILITY_REVIEW, NOTSTARTED } =
	PROGRESS_BAR_ELEMENTS;

const {
	ROLE_OPERATIONS_MANAGER,
	ROLE_SYSTEM_ADMINISTRATOR,
	ROLE_USER_ADMINISTRATOR,
} = LoggedInRole;

const {
	regulationInventory,
	overView,
	regulatoryApplicability,
	regulatoryGroupAssessment,
} = rrcpDashboardRoutes;

interface IAllRegulationsTableProps {
	id?: string;
	isLoading: boolean;
	setIsLoading?: any;
	inventoryTableData: IRegulationInventoryList[];
	getInventoryData: any;
	getApplicabilityData?: any;
	getGroupingData?: any;
	totalRecordsCount: number;
	errorMessage: string;
}

const AllRegulationsTable = (props: IAllRegulationsTableProps) => {
	const {
		id,
		isLoading,
		setIsLoading,
		inventoryTableData,
		getInventoryData,
		getGroupingData,
		getApplicabilityData,
		totalRecordsCount,
		errorMessage,
	} = props;

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const { tableQueryObj: tableProps, updateTableQuery } = useTableQuery({ id });
	const { tableQueryObj } = useTableQuery({ id: 'ApplicabilityRegTable' });
	const { updateQueryFlag } = useTableQuery({ id: 'AllRegTable' });

	const [selectedTheme, setSelectedTheme] = useState<string[]>([]);
	const [trigger, setTrigger] = useState<number>(0);
	const [ownerList, setOwnerList] = useState<IRegInvOwnerInfo[]>([]);

	const [bulkOwner, setBulkOwner] = useState<{
		assignedUserId?: string;
		assignedGroupId?: string;
	}>();
	const [bulkDueDate, setBulkDueDate] = useState<string>();

	const checkRole = isRoleExists([
		ROLE_OPERATIONS_MANAGER,
		ROLE_SYSTEM_ADMINISTRATOR,
		ROLE_USER_ADMINISTRATOR,
	]);
	const status = APPLICABILITY_ASSESSMENT;
	const regUniqueId = 'sectionId';

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			getInventoryData(data);
			updateTableQuery(data);
		}
	};

	const handleSortChange = (data: any) => {
		getInventoryData(data);
	};

	const onHandleCancel = useCallback(() => {
		setTrigger((trigger) => trigger + 1);
		setBulkDueDate('');
		setBulkOwner({});
	}, []);

	const getOwnerData = async (status: string) => {
		if (ownerList.find((res) => res.status === status)) {
			return;
		}
		const tempData = await getRegInvOwnerData(status);
		if (tempData) {
			setOwnerList((prev) => [...prev, tempData]);
		}
	};

	const getOwnerList = (status: string) => {
		let ownerValues: ISelectOption[] = [];
		ownerList.forEach((res) => {
			if (res.status === status) {
				ownerValues = res.owners;
			}
		});
		return ownerValues;
	};

	const onRowCheckboxSelect = (selectedId: any) => {
		if (selectedId) {
			setSelectedTheme(selectedId);
		}
		if (
			selectedId.length === 1 &&
			bulkDueDate &&
			(bulkOwner?.assignedGroupId || bulkOwner?.assignedUserId)
		) {
			setBulkDueDate('');
			setBulkOwner({});
		}
	};

	const selectedRegulations = inventoryTableData.filter((res) =>
		selectedTheme.includes(res.sectionId),
	);

	const hasSameStatus = selectedRegulations.every(
		(res) => res.taskStatus === selectedRegulations[0].taskStatus,
	);

	const multiSelectStatus =
		selectedRegulations.length > 0 && selectedRegulations[0].taskStatus;

	const onOwnerChange =
		(params: IRegulationInventoryList) => (value: string, obj: any) => {
			const newDueDate = moment().add(2, 'week').format(displayDateFormat);
			createUpdateRegulation(params, {
				taskOwner: obj,
				dueDate: params.reviewByDate ? params.reviewByDate : newDueDate,
			});
		};

	const onBulkOwnerChange = useCallback((value: string, obj: any) => {
		setBulkOwner(obj);
	}, []);

	const onDateChangeHandler =
		(params: IRegulationInventoryList) => (value: string) => {
			createUpdateRegulation(params, {
				taskOwner: null,
				dueDate: value,
			});
		};

	const onBulkDateChangeHandler = useCallback((value: string) => {
		setBulkDueDate(value);
	}, []);

	const onApplyClick = () => {
		const createTaskElements: ITask[] = [];
		const updateTaskElements: ITask[] = [];
		setTrigger((trigger) => trigger + 1);

		selectedTheme.forEach((element: string) => {
			const rowData = inventoryTableData.find(
				(obj: IRegulationInventoryList) => obj[regUniqueId] === element,
			);
			if (rowData && bulkDueDate) {
				if (rowData.taskId) {
					updateTaskElements.push(
						createTaskData(
							bulkOwner,
							bulkDueDate,
							rowData.sectionId,
							rowData.taskStatus,
							rowData.taskId,
						),
					);
				} else {
					createTaskElements.push(
						createTaskData(
							bulkOwner,
							bulkDueDate,
							rowData.sectionId,
							status,
							undefined,
							rowData.sectionName,
							'Medium',
						),
					);
				}
			}
		});
		createUpdateBulkRegulation(createTaskElements, updateTaskElements);
	};

	const createTaskData = (
		owner: any,
		dueDate: string,
		assignedObjectId: string,
		status: string,
		id?: string,
		taskName?: string,
		priorityLevel?: string,
	) => {
		const obj = {
			...(id ? { id } : {}),
			...(taskName ? { taskName } : {}),
			...(priorityLevel ? { priorityLevel } : {}),
			...(owner ? { ...owner } : {}),
			dueDate: getISODateString(dueDate),
			assignedObjectType: 'RegInv',
			assignedObjectId,
			status,
			taskType: WORKFLOW,
			moduleName: REQUIREMENT_INVENTORY_NAME,
		} as ITask;
		return obj;
	};

	const createRegulationTask = async (taskData: ITask[]) => {
		const response = await createTask(taskData);
		return response;
	};

	const updateRegulationTask = async (taskData: ITask[]) => {
		const response = await updateBulkTask(taskData);
		return response;
	};

	const navigationToThemeDetail = (sectionId: string, status: string) => {
		dispatch(setWorkflowFeedback([]));
		const uniqueData = sectionId;
		updateQueryFlag(true);
		dispatch(
			setRequirementInventoryBreadcrumbText(MODULES.REQUIREMENT_INVENTORY),
		);
		/// If status not present
		if (
			status === NOTSTARTED ||
			status === APPLICABILITY_ASSESSMENT ||
			status === APPLICABILITY_REVIEW
		) {
			navigate(
				urlWithTenant(
					`/${regulationInventory}/${overView}/${regulatoryApplicability}/${uniqueData}`,
				),
			);
		} else {
			navigate(
				urlWithTenant(
					`/${regulationInventory}/${overView}/${regulatoryGroupAssessment}/${uniqueData}`,
				),
			);
		}
	};

	const createUpdateRegulation = async (
		params: IRegulationInventoryList,
		updateValue: { taskOwner: any; dueDate?: string },
	) => {
		const { taskOwner, dueDate } = updateValue;
		let response: any;
		if (setIsLoading) {
			setIsLoading(true);
		}
		if (params.taskId) {
			const taskData = createTaskData(
				taskOwner,
				dueDate ?? params.reviewByDate,
				params.sectionId,
				params.taskStatus || status,
				params.taskId,
			);
			response = await updateRegulationTask([taskData]);
		} else {
			const taskData = createTaskData(
				taskOwner,
				dueDate ?? params.reviewByDate,
				params.sectionId,
				status,
				undefined,
				params.sectionName,
				'Medium',
			);
			response = await createRegulationTask([taskData]);
		}
		const toastMessageConfig = generateToastMessage(response);
		if (toastMessageConfig) {
			dispatch(
				setToastMessage({
					message: toastMessageConfig.message,
					isToastOpen: true,
					severity: toastMessageConfig.severity,
				}),
			);
		}
		if (response.statusCode === OK) {
			updateTableData(response?.responseBody);
			getApplicabilityData(tableQueryObj);
			getGroupingData(tableQueryObj);
		}
		if (setIsLoading) {
			setIsLoading(false);
		}
	};

	const updateTableData = (response: ICreateTask) => {
		const taskList = response.taskResponseList || [];
		inventoryTableData.forEach((res) => {
			taskList.forEach((val) => {
				if (res.sectionId === val.assignedObjectId) {
					res.reviewByDate = val.dueDate;
					res.assignedUserName = val.assignedUserFullName || '';
					res.taskStatus = val.status || '';
					res.ownerId = val.assignedUserId || '';
					res.taskId = val.id || '';
				}
			});
		});
	};

	const createUpdateBulkRegulation = async (
		createRecords: ITask[],
		updateRecords: ITask[],
	) => {
		let response: any;
		if (createRecords.length) {
			response = await createRegulationTask(createRecords);
		}
		if (updateRecords.length) {
			response = await updateRegulationTask(updateRecords);
		}
		const toastMessageConfig = generateToastMessage(response);
		if (toastMessageConfig) {
			dispatch(
				setToastMessage({
					message: toastMessageConfig.message,
					isToastOpen: true,
					severity: toastMessageConfig.severity,
				}),
			);
		}
		updateTableData(response?.responseBody);
	};

	const columns: readonly Column[] = [
		{
			id: 'displayId',
			label: 'Citation',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrpper'],
			render: (params: IRegulationInventoryList) => (
				<div className={styles['section-name']}>
					<Typography className={styles['text-section-common']}>
						<Tooltip title={params.displayId} placement="bottom-start">
							<span>{params.displayId}</span>
						</Tooltip>
					</Typography>
				</div>
			),
		},
		{
			id: 'displayRegulationTitle',
			label: 'Citaton title',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['text-styling-wrpper'],
			render: (params: IRegulationInventoryList) => (
				<div className={styles['section-name']}>
					<Typography
						className={styles['text-section']}
						data-testid="theme-link"
						onClick={() =>
							navigationToThemeDetail(params.sectionId, params.taskStatus)
						}
					>
						<Tooltip
							title={params.displayRegulationTitle}
							placement="bottom-start"
						>
							<span>{params.displayRegulationTitle}</span>
						</Tooltip>
					</Typography>
					{params.taskStatus !== COMPLETE && (
						<NotificationStatusIcon
							className={styles['icon-date']}
							date={params?.reviewByDate}
						/>
					)}
				</div>
			),
		},
		{
			id: 'jurisdiction',
			label: 'Jurisdiction',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'taskStatus',
			label: 'Workflow Status',
			disablePadding: false,
			isSortable: true,
		},
		{
			id: 'assignedUserName',
			label: 'Owner',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['owner-styling'],
			render: (params: IRegulationInventoryList) => (
				<Typography className={styles['text-section-common']}>
					{params.assignedUserName}
				</Typography>
			),
			isCellHoverEditable: checkRole && selectedTheme.length === 0,
			renderHoverEditCell: (params: IRegulationInventoryList) => {
				if (params.taskStatus) {
					getOwnerData(params.taskStatus);
					return (
						<SelectDropdown
							label="Owner"
							name={`name-${params.sectionId}`}
							id="regulation-owner"
							options={getOwnerList(params?.taskStatus)}
							mode="standard"
							showLabel={false}
							inputProps={{
								'data-testid': 'regulation-owner',
							}}
							value={params.ownerId}
							onChange={onOwnerChange(params)}
							className={styles['select-regulation-owner']}
						/>
					);
				}
				return (
					<Typography className={styles['text-section-common']}>
						{params.assignedUserName}
					</Typography>
				);
			},
			isCellEditable: checkRole && selectedTheme.length === 1,
			renderEditCell: (params: IRegulationInventoryList) => (
				<SelectDropdown
					label="Owner"
					name={`name-${params.sectionId}`}
					id="regulation-owner"
					options={getOwnerList(params?.taskStatus)}
					mode="standard"
					showLabel={false}
					inputProps={{
						'data-testid': 'regulation-owner',
					}}
					value={params.ownerId}
					onChange={onOwnerChange(params)}
					className={styles['select-regulation-owner']}
				/>
			),
		},
		{
			id: 'reviewByDate',
			label: 'Due Date',
			disablePadding: false,
			isSortable: true,
			cellClassName: styles['due-date-styling'],
			render: (params: any) => (
				<Typography className={styles['text-section-common']}>
					{params.reviewByDate
						? moment(getDateStringFromISOString(params.reviewByDate)).format(
								displayDateFormat,
						  )
						: ''}
				</Typography>
			),
			isCellHoverEditable: checkRole && selectedTheme.length === 0,
			renderHoverEditCell: (params: IRegulationInventoryList) => (
				<DatepickerComponent
					propType={{
						label: 'Due Date',
						disablePast: true,
						name: 'Review by',
						disabled: !params?.ownerId,
						id: 'reviewByDate',
						required: true,
					}}
					showLabel={false}
					mode="standard"
					onChange={onDateChangeHandler(params)}
					dateValue={
						params.reviewByDate
							? getDateStringFromISOString(params.reviewByDate)
							: undefined
					}
				/>
			),
			isCellEditable: checkRole && selectedTheme.length === 1,
			renderEditCell: (params: IRegulationInventoryList) => (
				<DatepickerComponent
					propType={{
						label: 'Due Date',
						disablePast: true,
						name: 'Review by',
						disabled: !params?.ownerId,
						id: 'reviewByDate',
						required: true,
					}}
					showLabel={false}
					mode="standard"
					onChange={onDateChangeHandler(params)}
					dateValue={
						params.reviewByDate
							? getDateStringFromISOString(params.reviewByDate)
							: undefined
					}
				/>
			),
		},
	];

	return (
		<Box data-testid="allregulations-main-box">
			<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
				<SelectableHOC
					count={selectedTheme.length}
					styleClass={styles['width-change']}
					element={
						<Box sx={{ display: 'flex' }} className={styles['hoc-dropdowns']}>
							{hasSameStatus && (
								<Box className={styles['flex-action-elements']}>
									<SelectDropdown
										label="Owner"
										name="owner"
										id="regulation-owner-multiselect"
										options={getOwnerList(multiSelectStatus as string)}
										mode="standard"
										inputProps={{
											'data-testid': 'regulation-owner-multiselect',
										}}
										value={
											bulkOwner?.assignedGroupId ?? bulkOwner?.assignedUserId
										}
										onChange={onBulkOwnerChange}
									/>
								</Box>
							)}
							<Box className={styles['flex-action-elements']}>
								<DatepickerComponent
									propType={{
										label: 'Due Date',
										disablePast: true,
										name: 'Review by',
										id: 'dueDate',
										required: true,
										inputProps: {
											'data-testid': 'reginv-dueDate',
										},
									}}
									mode="standard"
									onChange={onBulkDateChangeHandler}
									dateValue={bulkDueDate ?? ''}
								/>
							</Box>
							<Box className={styles['flex-action-button-elements']}>
								<ButtonComp
									btnType="primary"
									clickHandler={onApplyClick}
									compClass={styles['apply-button']}
									data-testid="bulk-apply-btn"
									disabled={
										!(hasSameStatus
											? (bulkOwner?.assignedGroupId ||
													bulkOwner?.assignedUserId) &&
											  bulkDueDate
											: bulkDueDate)
									}
								>
									Apply
								</ButtonComp>
							</Box>
						</Box>
					}
					handleCancelChange={onHandleCancel}
				>
					<Box className={styles['table-area']}>
						<TableComponent
							defaultQuery={tableProps}
							rows={inventoryTableData}
							columns={columns}
							totalItems={totalRecordsCount}
							isCheckBoxEnabled={checkRole}
							defaultSortKey="reviewByDate"
							defaultSortDirection="desc"
							rowHoverEnabled
							onPageHandler={handlePageChange}
							isLoading={isLoading}
							tableStyles={{ minWidth: '46.875em', maxHeight: '100vh' }}
							onSortHandler={handleSortChange}
							clearSelection={trigger}
							onRowChecked={onRowCheckboxSelect}
							rowKey={regUniqueId}
							customErrorMessage={errorMessage}
							enableHeaderColor
							isRenderPagination
						/>
					</Box>
				</SelectableHOC>
			</Grid>
		</Box>
	);
};
export default AllRegulationsTable;
